import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useResource } from "../lib/hooks";
import { useToast } from "../lib/toast";
import { ReferenceImagesField } from "../components/ReferenceImagesField";
import { ImageLightbox } from "../components/ImageLightbox";
import type { Setting } from "@flowgen/shared";

export function SettingsTab() {
  const res = useResource<Setting[]>(() => api.get("/settings"), []);
  const toast = useToast();
  const [editing, setEditing] = useState<Setting | null>(null);
  const [creating, setCreating] = useState(false);
  const [lightbox, setLightbox] = useState<{ urls: string[]; index: number } | null>(null);

  return (
    <div>
      <div className="workspace-header">
        <div>
          <h2>Settings</h2>
          <div className="sub">Named locations that anchor scene visuals.</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <button className="primary" onClick={() => setCreating(true)}>New setting</button>
        </div>
      </div>

      {res.loading && <div className="muted">Loading...</div>}
      {res.error && <div className="empty">{res.error}</div>}
      {!res.loading && !res.error && (res.data?.length ?? 0) === 0 && (
        <div className="empty">No settings yet.</div>
      )}

      {res.data && res.data.length > 0 && (
        <div className="grid cards">
          {res.data.map((s) => (
            <div key={s.id} className="card">
              <div className="row between">
                <h3>{s.name}</h3>
                <div className="row" style={{ gap: 4 }}>
                  <button onClick={() => setEditing(s)}>Edit</button>
                  <button className="danger" onClick={async () => {
                    if (!confirm(`Delete "${s.name}"?`)) return;
                    await api.delete(`/settings/${s.id}`);
                    res.reload();
                    toast.show("Setting deleted", "success");
                  }}>Delete</button>
                </div>
              </div>
              <div className="meta">{s.description || "No description"}</div>
              <div>
                {s.tags.map((t) => <span key={t} className="tag">{t}</span>)}
              </div>
              {s.referenceImageUrls.length > 0 && (
                <div className="image-grid">
                  {s.referenceImageUrls.map((u, idx) => (
                    <img
                      key={u}
                      src={api.mediaUrl(u)}
                      alt=""
                      onClick={() => setLightbox({ urls: s.referenceImageUrls, index: idx })}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <SettingDialog
          initial={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => {
            setEditing(null); setCreating(false); res.reload();
            toast.show(editing ? "Setting updated" : "Setting created", "success");
          }}
        />
      )}

      {lightbox && (
        <ImageLightbox
          urls={lightbox.urls}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onIndexChange={(index) => setLightbox((cur) => (cur ? { ...cur, index } : null))}
        />
      )}
    </div>
  );
}

interface SettingDialogProps {
  initial: Setting | null;
  onClose: () => void;
  onSaved: () => void;
}

function SettingDialog({ initial, onClose, onSaved }: SettingDialogProps) {
  const toast = useToast();
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [tagsInput, setTagsInput] = useState((initial?.tags ?? []).join(", "));
  const [images, setImages] = useState<string[]>(initial?.referenceImageUrls ?? []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function save() {
    if (!name.trim()) { toast.show("Name is required", "error"); return; }
    setSaving(true);
    try {
      const tags = tagsInput.split(",").map((s) => s.trim()).filter(Boolean);
      const body = { name: name.trim(), description, tags, referenceImageUrls: images };
      if (initial) await api.patch(`/settings/${initial.id}`, body);
      else await api.post("/settings", body);
      onSaved();
    } catch (err: any) {
      toast.show(err?.message ?? "Save failed", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <h3>{initial ? "Edit setting" : "New setting"}</h3>
        <div className="field">
          <label>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Neon Diner" />
        </div>
        <div className="field">
          <label>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Lighting, layout, era, mood..."
            rows={4}
          />
        </div>
        <div className="field">
          <label>Tags (comma separated)</label>
          <input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="indoor, neon, night, retro" />
        </div>
        <ReferenceImagesField
          images={images}
          onChange={setImages}
          defaultPrompt={description}
        />

        <div className="row between" style={{ marginTop: 16 }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={saving} onClick={save}>
            {saving ? "Saving..." : initial ? "Save changes" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
